// src/watchface/base.ts
// Absorbed from watchface-base.js (Task 32)

/** A clock time payload delivered to every watchface each frame. */
export interface ClockPayload {
  hours: number;
  minutes: number;
  seconds: number;
  ms: number;
  date: Date;
  hourDeg: number;
  minuteDeg: number;
  secondDeg: number;
  dateLabel: string;
}

/** A single collider segment passed from the lock-line renderer to the watchface. */
export interface LockLineColliderSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** Options for the lock-line collider update. */
export interface LockLineColliderOptions {
  enabled?: boolean;
  thickness?: number;
}

/** A config parameter descriptor used in the debug panel schema. */
export interface WatchfaceConfigParam {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  default: number;
  decimals?: number;
}

export class WatchfaceBase {
    container: HTMLElement;
    options: Record<string, unknown>;
    _dateFormatter: Intl.DateTimeFormat;

    constructor(container: HTMLElement, options: Record<string, unknown> = {}) {
        this.container = container;
        this.options = options;
        this._dateFormatter = new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
    }

    formatDate(date: Date): string {
        return this._dateFormatter.format(date).toLowerCase();
    }

    // Lifecycle
    mount(): void {}
    unmount(): void {}
    pause(_reason?: string): void {}
    resume(_pausedMs?: number): void {}

    // Time (always real-world values, never chrono-adjusted)
    update(_time: ClockPayload): void {}

    // Parallax
    setParallax(_x: number, _y: number, _source?: string): void {}

    // Zoom
    setZoomProgress(_progress: number): void {}

    // Minimal mode — 0 = full, 1 = hands only
    setMinimalMode(_progress: number): void {}

    // Chrono
    // onMidpoint is a function: animate to 12:00, call it, then STOP
    // onMidpoint is null: full flyback (both phases)
    chronoOut(_onMidpoint: (() => void) | null): void {}
    chronoIn(): void {}
    isChronoActive(): boolean { return false; }

    // IMU
    onMotion(_data: unknown): void {}

    // External geometry
    setLockLineColliders(_segments: LockLineColliderSegment[], _options: LockLineColliderOptions = {}): void {}

    // Configuration
    static getConfigSchema(): WatchfaceConfigParam[] { return []; }
    setConfig(_key: string, _value: number): void {}
    getConfig(): Record<string, number> { return {}; }
}
